import { appendIf, capitalizeFirstLetter } from '@btime/helpers'
import { assoc, compose, is, omit, pick, prop, propEq } from 'ramda'
import { clearFilterParams } from '@btime/react-contexts'
import { gql, useMutation } from '@apollo/client'
import { useNotification } from '@btime/react-messager'

import { exportsClient } from '../../../../utils/apollo/api'
import { hasValue } from '../../../../utils/helpers/value'
import { useCheckExport } from '../../../../utils/hooks/queueExport'
import { useCloseModal } from '../../../../components/modals/ModalsContext'
import { usePlaceTypes } from '../../../../utils/helpers/placeTypes'
import { cleanWhenEnabledFilterIsSet } from '../../../_helpers/filterByEnabled'

const TEMPLATE_FIELDS = {
  asset: ['name', 'type', 'place'],
  user: ['name', 'email', 'profile', 'group', 'is_fake_email'],
  serviceOrder: ['name', 'scheduling', 'user', 'place', 'checklist', 'description'],
  place: ['name', 'levels']
}

const cleanEnabledFilter = item => {
  return cleanWhenEnabledFilterIsSet(item)
}

const cleanFilter = compose(
  clearFilterParams, //
  item =>
    assoc(
      'checklistIds',
      is(Array, item?.checklistIds) ? item?.checklistIds : item?.checklistIds && [item?.checklistIds],
      item
    ),
  item => cleanEnabledFilter(item),
  omit(['checklistIds__error', 'insertedAt__error', 'scheduling__error', 'periodType', 'placeTypes__error'])
)

const cleanFields = item => {
  if (!!item.empty) return TEMPLATE_FIELDS[item.entity]
  if (item.fieldsConfig?.id === 'all') return undefined
  return item.fields?.map(prop('id'))
}

const cleanTemplate = template => {
  if (!template) return undefined

  return omit(['__typename', 'key'])(template)
}

const cleanRequest = compose(
  pick([
    'fields',
    'filter',
    'entity',
    'includeReplies',
    'includeBlockQuestions',
    'empty',
    'template',
    'bulkResumeExport'
  ]), //
  item => assoc('fields', cleanFields(item), item),
  item => assoc('filter', cleanFilter(item.filter), item),
  item => assoc('template', cleanTemplate(item.template), item),
  item => assoc('bulkResumeExport', item.bulkResumeExport, item)
)

const validate = (form, setErrors) => {
  const isEmpty = form.empty
  const isServiceOrder = form.entity === 'serviceOrder'
  const hasPlaceType = ['serviceOrder', 'asset'].includes(form.entity)
  let errors
  if (form.template) {
    errors = [
      ...appendIf(!isEmpty && isServiceOrder && form.filter?.periodType === 'insertedAt' && !form.filter?.insertedAt, {
        path: ['filter', 'insertedAt'],
        message: 'Campo obrigatório'
      }),
      ...appendIf(!isEmpty && isServiceOrder && form.filter?.periodType === 'scheduling' && !form.filter?.scheduling, {
        path: ['filter', 'scheduling'],
        message: 'Campo obrigatório'
      })
    ]
  } else {
    errors = [
      ...appendIf(!isEmpty && isServiceOrder && form.filter?.periodType === 'insertedAt' && !form.filter?.insertedAt, {
        path: ['filter', 'insertedAt'],
        message: 'Campo obrigatório'
      }),
      ...appendIf(!isEmpty && isServiceOrder && form.filter?.periodType === 'scheduling' && !form.filter?.scheduling, {
        path: ['filter', 'scheduling'],
        message: 'Campo obrigatório'
      }),

      ...appendIf(!isEmpty && isServiceOrder && form.includeReplies && !hasValue(form.filter?.checklistIds), {
        path: ['filter', 'checklistIds'],
        message: 'Campo obrigatório'
      }),

      ...appendIf(
        !isEmpty &&
          isServiceOrder &&
          form.includeReplies &&
          !!hasValue(form.filter?.checklistIds) &&
          form.filter?.checklistIds.length > 1,
        {
          path: ['filter', 'checklistIds'],
          message: 'Selecione somente 1'
        }
      ),

      ...appendIf(isEmpty && hasPlaceType && !hasValue(form.filter?.placeTypes), {
        path: ['filter', 'placeTypes'],
        message: 'Campo obrigatório'
      })
    ]
  }

  setErrors(errors)
  return errors.length === 0
}

export const useExport = entity => {
  const [placeTypes] = usePlaceTypes()
  entity = placeTypes.some(propEq('key', entity)) ? 'place' : entity
  const module = capitalizeFirstLetter(entity)

  const MUTATION = gql`
    mutation Export($template: TemplateInput, $empty: Boolean, $filter: ${module}Filter, $fields: [Dynamic], $entity: String,
     ${
       entity === 'serviceOrder'
         ? '$includeReplies: Boolean, $bulkResumeExport: Boolean, $includeBlockQuestions: Boolean'
         : ''
     }) {
      export${module}(empty: $empty, filter: $filter, fields: $fields, input: { entity: $entity, template: $template }, 
      ${
        entity === 'serviceOrder'
          ? 'includeReplies: $includeReplies, bulkResumeExport: $bulkResumeExport, includeBlockQuestions: $includeBlockQuestions'
          : ''
      }) {
        id
      }
    }
  `

  const [request, state] = useMutation(MUTATION, {
    refetchQueries: ['Exports'],
    client: exportsClient
  })

  const checkExport = useCheckExport()

  const notify = useNotification()
  const closeModal = useCloseModal()

  const handleRequest = (form, actions) => {
    const input = cleanRequest(form)
    const { setErrors } = actions
    const isValid = validate(form, setErrors)
    const bulkResumeExport = form?.bulkResumeExport

    if (isValid) {
      const notificationId = notify.loading({ title: 'Enviando pedido...' })
      const times = 1

      return request({ variables: input }).then(resp => {
        if (!resp.errors) {
          notify.updateToLoading(notificationId, {
            title: 'Gerando arquivo...',
            ...(bulkResumeExport && { message: 'Enviaremos o arquivo diretamente para o seu e-mail' })
          })

          checkExport(resp?.data[`export${module}`]?.id, notificationId, times, bulkResumeExport)

          if (closeModal) closeModal()
        } else {
          notify.updateToError(notificationId, { title: 'Ocorreu um erro ao exportar!' })
        }
      })
    }
  }

  return [handleRequest, state]
}
